"use client";

import * as stylex from "@stylexjs/stylex";
import "@/colors.stylex";
import { colors } from "@/colors.stylex";
import { DirectHapticTarget } from "@/components/direct-haptic-target";
import { GoBoard, goBoardStyles } from "@/components/go-board";
import { useMokaMatchContext } from "@/components/moka-match/moka-match-root";
import { Button } from "@/components/ui/button";
import { GO_BOARD_GRID_LINE_INDEXES, GO_BOARD_GRID_STEP_PERCENT, MOKA_MATCH_ANALYSIS_ADVANTAGE_CHANGE_THRESHOLD, MOKA_MATCH_ANALYSIS_TILE_SIZE_PERCENT, MOKA_MATCH_INTERSECTION_HIT_TARGET_PERCENT, MOKA_MATCH_INVALID_MOVE_ANIMATION_DURATION_MS, MOKA_MATCH_INVALID_MOVE_TRANSLATION_REM } from "@/constants";
import { formatMokaMoveWinProbability } from "@/utils/format-moka-move-win-probability";
import { getMokaCoordinateLabel } from "@/utils/get-moka-coordinate-label";
import { getNearestGoBoardCoordinate } from "@/utils/get-nearest-go-board-coordinate";
const mokaInvalidMove = "x1kfloll-B";
const _temp = {
  kbCHJM: "xgq9j65",
  k87sOh: "xjbys53",
  "$$css": true
};
const _temp2 = {
  kzqmXN: "x5lhr3w",
  kZKoxP: "x16ye13r",
  "$$css": true
};
const styles = {
  boardArea: {
    kVAEAm: "x1n2onr6",
    "--moka-board-overlay-opacity": "xcd9f5b x5dzwde xt524au",
    $$css: true
  },
  boardAreaDisabled: {
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  boardAreaIllegal: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  boardAreaPlayable: {
    kkrTdU: "xsid9ct",
    $$css: true
  },
  invalidMove: {
    kKVMdj: "x1rwwj71 x1aquc0h",
    kyAemX: "x4hg4is",
    $$css: true
  },
  debugTerritoryMarker: {
    kfzvcC: "x47corl",
    kVAEAm: "x10l6tqk",
    kzqmXN: "x6vlp7l",
    kZKoxP: "x1vpwmf9",
    kIY38u: "x113ijq",
    kaIpWk: "x16eypsj",
    $$css: true
  },
  blackTerritoryMarker: {
    kWkggS: "x1artdhm",
    $$css: true
  },
  whiteTerritoryMarker: {
    kWkggS: "x1u23wyf",
    kGVxlE: "xulcpbr",
    $$css: true
  },
  gridPosition: (leftPercent, topPercent) => [_temp, {
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${leftPercent}%`),
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${topPercent}%`)
  }],
  squareSize: sizePercent => [_temp2, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${sizePercent}%`),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${sizePercent}%`)
  }],
  analysisTile: {
    kfzvcC: "x47corl",
    kVAEAm: "x10l6tqk",
    kY2c9j: "x1n327nk",
    k1xSpc: "x78zum5",
    kIY38u: "x113ijq",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kaIpWk: "x6i6fhv",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kMv6JI: "xmotr4s",
    kGuDYH: "xvwhms9 xrx0dd7",
    kLWn49: "xo5v014",
    k63SB2: "xo1l8bm",
    kMwMTN: "xo6u9xg",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  analysisTileGain: {
    kVAM5u: "x1fc66d7",
    kWkggS: "x8q0cvi",
    $$css: true
  },
  analysisTileEven: {
    kVAM5u: "xdh97nt",
    kWkggS: "x6eo33c",
    $$css: true
  },
  analysisTileLoss: {
    kVAM5u: "xnvl692",
    kWkggS: "x1ie2jmw",
    $$css: true
  },
  hoverStone: {
    kfzvcC: "x47corl",
    kY2c9j: "x1355qak",
    kIyJzY: "xx6bhzk",
    $$css: true
  },
  hoverStoneLegal: {
    kWkggS: "x1jqvp7",
    kGVxlE: "x3x02gr",
    $$css: true
  },
  hoverStoneIllegal: {
    kWkggS: "x1gs5ha7",
    kGVxlE: "x1pk7ps8",
    $$css: true
  },
  intersection: {
    kVAEAm: "x10l6tqk",
    kIY38u: "x113ijq",
    kaIpWk: "xtkphhm",
    $$css: true
  },
  intersectionButton: {
    kkrTdU: "x1ypdohk",
    kWkggS: "xpvow8l",
    kMeerF: "x784prv",
    k3XXqK: "x1t137rt x9v5kkp",
    kInvED: "x13iak60",
    kjBf7l: "x1s0p2oj",
    kfzvcC: "xaqnwrm",
    $$css: true
  },
  hapticIntersection: {
    kMeerF: "x1odlxkb",
    k3XXqK: "x1t137rt xnnj0qo",
    kInvED: "xtsxt1s",
    kjBf7l: "x1a1hf4r",
    $$css: true
  },
  hapticIntersectionHover: {
    "--moka-intersection-hover-background": "xpzuueo",
    $$css: true
  },
  overlayButton: {
    kfzvcC: "x67bb7w",
    kWkggS: "xp2616n x166iri9",
    kg3NbH: "x1qhpy6z",
    kGVxlE: "x1qrsuq9",
    $$css: true
  }
};
const getCoordinatePositionStyle = coordinate => styles.gridPosition(coordinate.column * GO_BOARD_GRID_STEP_PERCENT, coordinate.row * GO_BOARD_GRID_STEP_PERCENT);
const getMokaMoveAnalysisToneStyle = (blackWinProbability, currentBlackWinProbability) => {
  const blackWinProbabilityChange = blackWinProbability - currentBlackWinProbability;
  if (blackWinProbabilityChange >= MOKA_MATCH_ANALYSIS_ADVANTAGE_CHANGE_THRESHOLD) {
    return styles.analysisTileGain;
  }
  if (blackWinProbabilityChange > -MOKA_MATCH_ANALYSIS_ADVANTAGE_CHANGE_THRESHOLD) {
    return styles.analysisTileEven;
  }
  return styles.analysisTileLoss;
};
const MokaMatchTerritoryMarkers = ({
  color,
  coordinates
}) => coordinates.map(coordinate => <span aria-hidden="true" key={`${color}-territory-${coordinate.column}-${coordinate.row}`} {...stylex.props(styles.debugTerritoryMarker, color === "black" ? styles.blackTerritoryMarker : styles.whiteTerritoryMarker, getCoordinatePositionStyle(coordinate))} />);
MokaMatchTerritoryMarkers.displayName = "MokaMatchTerritoryMarkers";
const MokaMatchAnalysisTiles = () => {
  const {
    blackMoveAnalyses,
    isAnalysisModeActive,
    position
  } = useMokaMatchContext();
  if (!isAnalysisModeActive) {
    return null;
  }
  return blackMoveAnalyses.map(moveAnalysis => <span aria-hidden="true" data-black-win-probability={moveAnalysis.blackWinProbability} data-moka-move-analysis="" key={`analysis-${moveAnalysis.coordinate.column}-${moveAnalysis.coordinate.row}`} {...stylex.props(styles.analysisTile, getMokaMoveAnalysisToneStyle(moveAnalysis.blackWinProbability, position.blackWinProbability), styles.squareSize(MOKA_MATCH_ANALYSIS_TILE_SIZE_PERCENT), getCoordinatePositionStyle(moveAnalysis.coordinate))}>
      {formatMokaMoveWinProbability(moveAnalysis.blackWinProbability)}
    </span>);
};
MokaMatchAnalysisTiles.displayName = "MokaMatchAnalysisTiles";
const MokaMatchHoverStone = () => {
  const {
    hoveredCoordinate,
    isBoardDisabled,
    isHoveredMoveIllegal
  } = useMokaMatchContext();
  if (!hoveredCoordinate || isBoardDisabled) {
    return null;
  }
  return <span aria-hidden="true" {...stylex.props(goBoardStyles.stone, goBoardStyles.stoneSize, styles.hoverStone, isHoveredMoveIllegal ? styles.hoverStoneIllegal : styles.hoverStoneLegal, getCoordinatePositionStyle(hoveredCoordinate))} />;
};
MokaMatchHoverStone.displayName = "MokaMatchHoverStone";
const MokaMatchIntersection = ({
  coordinate
}) => {
  const {
    doesNeedDirectHapticTarget,
    isBoardDisabled,
    playCoordinate,
    position
  } = useMokaMatchContext();
  const coordinateLabel = getMokaCoordinateLabel(coordinate);
  const stone = position.gameState.board[coordinate.row]?.[coordinate.column];
  const accessibleLabel = stone ? `${coordinateLabel}, ${stone.color} stone` : `Play at ${coordinateLabel}`;
  const intersectionPlacementStyles = [styles.squareSize(MOKA_MATCH_INTERSECTION_HIT_TARGET_PERCENT), getCoordinatePositionStyle(coordinate)];
  if (doesNeedDirectHapticTarget) {
    return <DirectHapticTarget accessibleLabel={accessibleLabel} disabled={isBoardDisabled} onActivate={() => playCoordinate(coordinate, true)} styles={[styles.intersection, styles.hapticIntersection, !isBoardDisabled && styles.hapticIntersectionHover, ...intersectionPlacementStyles]}>
        <span aria-hidden="true" className="x47corl x10l6tqk x10a8y8t xtkphhm xti97al" />
      </DirectHapticTarget>;
  }
  return <button aria-label={accessibleLabel} disabled={isBoardDisabled || stone !== null} onClick={event => {
    event.stopPropagation();
    playCoordinate(coordinate);
  }} type="button" {...stylex.props(styles.intersection, styles.intersectionButton, ...intersectionPlacementStyles)} />;
};
MokaMatchIntersection.displayName = "MokaMatchIntersection";
const MokaMatchDebugOverlay = () => {
  const {
    isDebugMode,
    position,
    startPlayableGame
  } = useMokaMatchContext();
  if (!isDebugMode || position.isGameComplete) {
    return null;
  }
  return <div className="x47corl x10l6tqk x10a8y8t x1355qak x1s85apg x1x25rye x6s0dn4 xl56j7k x1wyqy5d x19991ni x4wkmsb x13dflua xwwtwea">
      <Button onClick={event => {
      event.stopPropagation();
      startPlayableGame();
    }} size="default" styles={styles.overlayButton} type="button" variant="outline">
        Play against Moka
      </Button>
    </div>;
};
MokaMatchDebugOverlay.displayName = "MokaMatchDebugOverlay";
const MokaMatchBoard = () => {
  const {
    areaAnalysis,
    clearHoveredCoordinate,
    clearInvalidMove,
    hoverCoordinate,
    invalidMoveCoordinate,
    isBoardDisabled,
    isDebugMode,
    isHoveredMoveIllegal,
    latestMove,
    playCoordinate,
    position,
    setIsAnalysisShortcutActive
  } = useMokaMatchContext();
  const boardAreaProps = stylex.props(styles.boardArea, isBoardDisabled ? styles.boardAreaDisabled : isHoveredMoveIllegal ? styles.boardAreaIllegal : styles.boardAreaPlayable, invalidMoveCoordinate && styles.invalidMove);
  return <div {...boardAreaProps} onAnimationEnd={clearInvalidMove} onClick={event => {
    if (isBoardDisabled) {
      return;
    }
    playCoordinate(getNearestGoBoardCoordinate(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect()));
  }} onPointerLeave={clearHoveredCoordinate} onPointerMove={event => {
    setIsAnalysisShortcutActive(event.shiftKey);
    hoverCoordinate(getNearestGoBoardCoordinate(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect()));
  }} style={{
    ...boardAreaProps.style,
    "--moka-invalid-move-offset": `${MOKA_MATCH_INVALID_MOVE_TRANSLATION_REM}rem`,
    animationDuration: `${MOKA_MATCH_INVALID_MOVE_ANIMATION_DURATION_MS}ms`
  }}>
      <div>
        <GoBoard aria-hidden="true" isAnimated latestMove={latestMove} size="fluid" stones={position.stones} />

        <div {...stylex.props(goBoardStyles.grid)}>
          {isDebugMode && <>
              <MokaMatchTerritoryMarkers color="black" coordinates={areaAnalysis.blackTerritoryCoordinates} />
              <MokaMatchTerritoryMarkers color="white" coordinates={areaAnalysis.whiteTerritoryCoordinates} />
            </>}
          <MokaMatchAnalysisTiles />
          <MokaMatchHoverStone />
          {GO_BOARD_GRID_LINE_INDEXES.flatMap(row => GO_BOARD_GRID_LINE_INDEXES.map(column => <MokaMatchIntersection coordinate={{
          column,
          row
        }} key={getMokaCoordinateLabel({
          column,
          row
        })} />))}
        </div>
      </div>

      <MokaMatchDebugOverlay />
    </div>;
};
MokaMatchBoard.displayName = "MokaMatchBoard";
export { MokaMatchBoard };